import { Matrix, CellBase } from 'react-spreadsheet';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util, CreditAgreement } from '@finley-technologies/finley-types';

export type PrimitiveCellType = CellBase<Util.Primitive>;
export type MatrixType = Matrix<PrimitiveCellType>;

type DataCellObject = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObject;

const BUFFER_LENGTH = 2;
export const DEFAULT_DATA_LENGTH = 10;

export const createEmptyCell = () => ({ value: '' });

export const createMatrixRow = (len?: number): PrimitiveCellType[] => {
  return new Array(len ?? DEFAULT_DATA_LENGTH).fill(null).map(createEmptyCell);
};

export const createDefaultData = (
  lens: { r?: number; c?: number } = {},
): MatrixType => {
  const { r = DEFAULT_DATA_LENGTH, c = DEFAULT_DATA_LENGTH } = lens;
  return new Array(r).fill(null).map(() => createMatrixRow(c));
};

export const translateDataCellObjectsToSpreadsheet = (
  dataCells?: DataCellObject[],
): MatrixType => {
  if (!dataCells || dataCells.length === 0) {
    return [];
  }

  let maxRow = -1;
  let maxCol = -1;
  for (const { numberCoordinates } of dataCells) {
    const { column, row } = numberCoordinates;
    // "column" and "row" are 0-indexed so adding one to translate to spreadsheet
    maxRow = Math.max(maxRow, row + 1);
    maxCol = Math.max(maxCol, column + 1);
  }

  const dataMatrix: MatrixType = new Array(
    Math.max(maxRow + BUFFER_LENGTH, DEFAULT_DATA_LENGTH),
  )
    .fill(null)
    .map(() =>
      createMatrixRow(Math.max(maxCol + BUFFER_LENGTH, DEFAULT_DATA_LENGTH)),
    );

  for (const { value, numberCoordinates } of dataCells) {
    const { column, row } = numberCoordinates;
    dataMatrix[row][column] = { value };
  }

  return dataMatrix;
};

export const sortDataCellObjects = (
  dataCellObjects: DataCellObject[],
): DataCellObject[] => {
  return [...dataCellObjects].sort(
    ({ numberCoordinates: aCoords }, { numberCoordinates: bCoords }) => {
      if (aCoords.row !== bCoords.row) {
        return aCoords.row - bCoords.row;
      }
      return aCoords.column - bCoords.column;
    },
  );
};

export const translateSpreadsheetToDataCellObjects = (
  dataMatrix: MatrixType,
): DataCellObject[] => {
  const dataCellArray: DataCellObject[] = [];

  dataMatrix.forEach((row, rowIdx) => {
    row.forEach((cell, colIdx) => {
      if (!cell || cell.value === '' || cell.value === undefined) {
        return;
      }

      const excelCoordinate = commonUtils.numberCoordsToExcelCoords({
        r: rowIdx,
        c: colIdx,
      });
      dataCellArray.push({
        value: cell.value,
        excelCoordinate,
        numberCoordinates: { column: colIdx, row: rowIdx },
      });
    });
  });

  return sortDataCellObjects(dataCellArray);
};
